import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface CacheEntry {
  data: any[];
  timestamp: number;
}

interface AttendanceCountsData {
  totalEmployee?: { totalEmployee: number; totalRegisteredEmp: number; totalContractEmpCount: number };
  presentToday?: { totalPresentToday: number; totalPresentRegToday: number; totalContractualPresentToday: number };
  absentToday?: { totalAbsentToday: number; totalAbsentRegToday: number; totalContractualAbsentToday: number };
  previousDay?: { totalPreviousDayAttendance: number; totalPreviousDayAttendanceReg: number; totalPreviousDayContractualAttendance: number };
}

interface AttendanceCacheContextType {
  regularCache: Map<string, CacheEntry>;
  contractualCache: Map<string, CacheEntry>;
  countsData: AttendanceCountsData;
  setCachedRegularData: (pageOrKey: number | string, data: any[]) => void;
  setCachedContractualData: (pageOrKey: number | string, data: any[]) => void;
  getCachedRegularData: (pageOrKey: number | string) => any[] | null;
  getCachedContractualData: (pageOrKey: number | string) => any[] | null;
  setCachedCounts: (key: keyof AttendanceCountsData, data: any) => void;
  clearCache: () => void;
  isCacheValid: (timestamp: number, maxAge?: number) => boolean;
}

const AttendanceCacheContext = createContext<AttendanceCacheContextType | undefined>(undefined);

// Cache validity in milliseconds (20 minutes)
const CACHE_VALIDITY = 20 * 60 * 1000;

export function AttendanceCacheProvider({ children }: { children: React.ReactNode }) {
  const [regularCache] = useState(() => new Map<string, CacheEntry>());
  const [contractualCache] = useState(() => new Map<string, CacheEntry>());
  const [, setCacheUpdateTrigger] = useState(0);
  const [countsData, setCountsData] = useState<AttendanceCountsData>(() => {
    try {
      const stored = sessionStorage.getItem('attendanceCountsData');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const isCacheValid = useCallback((timestamp: number, maxAge = CACHE_VALIDITY) => {
    return Date.now() - timestamp < maxAge;
  }, []);

  const toKey = useCallback((key: number | string) => String(key), []);

  const setCachedRegularData = useCallback((pageOrKey: number | string, data: any[]) => {
    const key = toKey(pageOrKey);
    regularCache.set(key, { data, timestamp: Date.now() });
    // console.log(`💾 Cached Regular attendance data for key: ${key}`);
    setCacheUpdateTrigger(prev => prev + 1); // Trigger re-render
  }, [regularCache, toKey]);

  const setCachedContractualData = useCallback((pageOrKey: number | string, data: any[]) => {
    const key = toKey(pageOrKey);
    contractualCache.set(key, { data, timestamp: Date.now() });
    // console.log(`💾 Cached Contractual attendance data for key: ${key}`);
    setCacheUpdateTrigger(prev => prev + 1); // Trigger re-render
  }, [contractualCache, toKey]);

  const getCachedRegularData = useCallback((pageOrKey: number | string) => {
    const key = toKey(pageOrKey);
    const entry = regularCache.get(key);
    if (entry && isCacheValid(entry.timestamp)) {
      // console.log(`✅ Cache HIT for Regular key: ${key}`);
      return entry.data;
    }
    if (entry) {
      // console.log(`⏰ Cache EXPIRED for Regular key: ${key}`);
      regularCache.delete(key); // Remove stale cache
    } else {
      // console.log(`❌ Cache MISS for Regular key: ${key}`);
    }
    return null;
  }, [regularCache, isCacheValid, toKey]);

  const getCachedContractualData = useCallback((pageOrKey: number | string) => {
    const key = toKey(pageOrKey);
    const entry = contractualCache.get(key);
    if (entry && isCacheValid(entry.timestamp)) {
      // console.log(`✅ Cache HIT for Contractual key: ${key}`);
      return entry.data;
    }
    if (entry) {
      // console.log(`⏰ Cache EXPIRED for Contractual key: ${key}`);
      contractualCache.delete(key); // Remove stale cache
    } else {
      // console.log(`❌ Cache MISS for Contractual key: ${key}`);
    }
    return null;
  }, [contractualCache, isCacheValid, toKey]);

  const setCachedCounts = useCallback((key: keyof AttendanceCountsData, data: any) => {
    setCountsData(prev => {
      const updated = { ...prev, [key]: data };
      try {
        sessionStorage.setItem('attendanceCountsData', JSON.stringify(updated));
      } catch {
        // Silently fail
      }
      return updated;
    });
  }, []);

  const clearCache = useCallback(() => {
    regularCache.clear();
    contractualCache.clear();
    setCountsData({});
    try {
      sessionStorage.removeItem('attendanceCountsData');
    } catch {
      // Silently fail
    }
    setCacheUpdateTrigger(prev => prev + 1);
  }, [regularCache, contractualCache]);

  const contextValue = useMemo(() => ({
    regularCache,
    contractualCache,
    countsData,
    setCachedRegularData,
    setCachedContractualData,
    getCachedRegularData,
    getCachedContractualData,
    setCachedCounts,
    clearCache,
    isCacheValid,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [regularCache, contractualCache, countsData, setCachedRegularData, setCachedContractualData, getCachedRegularData, getCachedContractualData, setCachedCounts, clearCache, isCacheValid]);

  return (
    <AttendanceCacheContext.Provider value={contextValue}>
      {children}
    </AttendanceCacheContext.Provider>
  );
}

export function useAttendanceCache() {
  const context = useContext(AttendanceCacheContext);
  if (!context) {
    throw new Error('useAttendanceCache must be used within AttendanceCacheProvider');
  }
  return context;
}
